import { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  AlertTriangle,
  Banknote,
  CheckCircle2,
  Coins,
  HardDrive,
  Hourglass,
  Layers,
  Power,
  Printer,
  RefreshCw,
  Settings2,
  ShieldAlert,
  Timer,
  WifiOff,
  XCircle,
} from "lucide-react";

import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import type { CashPaymentStatusView } from "@/features/payment/cash-payment-status";
import {
  useCashDriversStatus,
  type CashDriverStatusResult,
  type CashDriversStatus,
  type PrinterDiagnosticStatus,
  type PrinterEventDetails,
  type PrinterStatusResult,
} from "@/hooks/use-cash-drivers-status";
import { usePaymentStatus } from "@/hooks/use-payment-status";
import { useFeatureFlags } from "@/lib/runtime-config";
import { cn } from "@/lib/utils";

type EventSource = "pay-in" | "pay-out";

type LogEntry = {
  id: string;
  ts: number;
  source: EventSource;
  type: string;
  data: unknown;
};

type NormalizedPrinterStatus = Extract<
  PrinterStatusResult,
  { ok: true }
>["status"];

const TOAST_DURATION_MS = 4500;
const MAX_VISIBLE_TOASTS = 4;

export function CashPaymentDebugPanel() {
  const flags = useFeatureFlags();
  const paymentQuery = usePaymentStatus();
  const status = paymentQuery.data;
  const hasActivePayment = !!status && !status.sideEffects.completed;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const driversQuery = useCashDriversStatus({
    enabled: flags.cashPaymentDebug && !hasActivePayment && drawerOpen,
  });

  const [log, setLog] = useState<LogEntry[]>([]);
  const [toasts, setToasts] = useState<LogEntry[]>([]);
  const seenRef = useRef<{ key: string; payIn: number; payOut: number }>({
    key: "",
    payIn: 0,
    payOut: 0,
  });

  useEffect(() => {
    if (!status) return;

    if (seenRef.current.key !== status.paymentKey) {
      seenRef.current = {
        key: status.paymentKey,
        payIn: 0,
        payOut: 0,
      };
      setLog([]);
      setToasts([]);
    }

    const fresh: LogEntry[] = [];
    const now = Date.now();

    const inEvents = [...status.debug.payInEvents];
    for (let i = seenRef.current.payIn; i < inEvents.length; i++) {
      const e = inEvents[i]!;
      fresh.push({
        id: `${status.paymentKey}-in-${i}`,
        ts: now + i,
        source: "pay-in",
        type: e.type,
        data: e.data,
      });
    }
    seenRef.current.payIn = inEvents.length;

    const outEvents = [...status.debug.payOutEvents];
    for (let i = seenRef.current.payOut; i < outEvents.length; i++) {
      const e = outEvents[i]!;
      fresh.push({
        id: `${status.paymentKey}-out-${i}`,
        ts: now + i,
        source: "pay-out",
        type: e.type,
        data: e.data,
      });
    }
    seenRef.current.payOut = outEvents.length;

    if (fresh.length > 0) {
      setLog((prev) => [...prev, ...fresh]);
      setToasts((prev) => [...prev, ...fresh]);
    }
  }, [status]);

  if (!flags.cashPaymentDebug) {
    return null;
  }

  const dismissToast = (id: string) =>
    setToasts((prev) => prev.filter((t) => t.id !== id));

  const visibleToasts = toasts.slice(-MAX_VISIBLE_TOASTS);
  const drivers = driversQuery.data;
  const printerHealthy =
    !!drivers &&
    drivers.printer.ok &&
    printerStatusIsHealthy(drivers.printer.status);
  const driversIndicatorTone = !drivers
    ? "idle"
    : drivers.coin.ok &&
        drivers.note.ok &&
        !drivers.coin.status.error &&
        !drivers.note.status.error &&
        printerHealthy
      ? "ok"
      : "err";

  return (
    <>
      <div className="pointer-events-none fixed inset-x-0 top-3 z-[60] flex flex-col items-center gap-2 px-4">
        {visibleToasts.map((entry) => (
          <ToastCard
            key={entry.id}
            entry={entry}
            onDismiss={() => dismissToast(entry.id)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => setDrawerOpen(true)}
        className="fixed bottom-5 right-5 z-[55] flex items-center gap-2.5 rounded-full bg-white py-2.5 pl-3.5 pr-4 shadow-[0_12px_30px_-10px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/80 transition active:scale-95"
      >
        <span className="relative inline-flex size-2.5">
          {hasActivePayment && (
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-teal-400 opacity-60" />
          )}
          <span
            className={cn(
              "relative inline-flex size-2.5 rounded-full",
              hasActivePayment
                ? "bg-teal-500 shadow-[0_0_10px_rgba(20,184,166,0.7)]"
                : driversIndicatorTone === "ok"
                  ? "bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.6)]"
                  : driversIndicatorTone === "err"
                    ? "bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.6)]"
                    : "bg-slate-300",
            )}
          />
        </span>
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-700">
          Diag
        </span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[11px] tabular-nums text-slate-600">
          {hasActivePayment
            ? log.length
            : driversIndicatorTone === "ok"
              ? "OK"
              : driversIndicatorTone === "err"
                ? "HIBA"
                : "···"}
        </span>
      </button>

      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen} direction="right">
        <DrawerContent className="!max-w-md border-l border-slate-200 bg-gradient-to-b from-white to-slate-50">
          {status ? (
            <DiagDrawerBody status={status} log={log} />
          ) : (
            <IdleDrawerBody drivers={drivers} error={driversQuery.error} />
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
}

function ToastCard({
  entry,
  onDismiss,
}: {
  entry: LogEntry;
  onDismiss: () => void;
}) {
  const [progress, setProgress] = useState(100);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    const start = requestAnimationFrame(() => setProgress(0));
    const timer = setTimeout(() => onDismissRef.current(), TOAST_DURATION_MS);
    return () => {
      cancelAnimationFrame(start);
      clearTimeout(timer);
    };
  }, []);

  const isPayIn = entry.source === "pay-in";
  const meta = describeEvent(entry.type, entry.data);

  return (
    <div
      onClick={onDismiss}
      className="pointer-events-auto group relative w-full max-w-[420px] cursor-pointer overflow-hidden rounded-2xl bg-white shadow-[0_18px_44px_-18px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/80 backdrop-blur-sm animate-in slide-in-from-top-2 fade-in duration-300"
    >
      <div
        className={cn(
          "absolute inset-y-0 left-0 w-1",
          isPayIn ? "bg-teal-400" : "bg-amber-400",
        )}
      />
      <div className="px-4 py-3 pl-5">
        <div className="mb-0.5 flex items-baseline justify-between gap-3">
          <span
            className={cn(
              "font-mono text-[10px] font-semibold uppercase tracking-[0.18em]",
              isPayIn ? "text-teal-600" : "text-amber-600",
            )}
          >
            {isPayIn ? "Befizetés" : "Visszafizetés"}
          </span>
          <span className="font-mono text-[10px] text-slate-400 tabular-nums">
            {format(entry.ts, "HH:mm:ss")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <meta.Icon className={cn("size-4", meta.iconClass)} />
          <span className="text-[14px] font-semibold text-slate-800">
            {meta.label}
          </span>
        </div>
        {meta.summary && (
          <div className="mt-1 line-clamp-2 break-words text-[11.5px] leading-snug text-slate-500">
            {meta.summary}
          </div>
        )}
      </div>
      <div
        className={cn(
          "h-[2px] transition-[width] ease-linear",
          isPayIn ? "bg-teal-400/70" : "bg-amber-400/70",
        )}
        style={{
          width: `${progress}%`,
          transitionDuration: `${TOAST_DURATION_MS}ms`,
        }}
      />
    </div>
  );
}

function IdleDrawerBody({
  drivers,
  error,
}: {
  drivers: CashDriversStatus | undefined;
  error: unknown;
}) {
  return (
    <>
      <DrawerHeader className="border-b border-slate-200 bg-white/60">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="relative inline-flex size-2.5">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-slate-300 opacity-60" />
              <span className="relative inline-flex size-2.5 rounded-full bg-slate-400" />
            </span>
            <DrawerTitle className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-700">
              Készpénz · Hardver
            </DrawerTitle>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
            <RefreshCw className="size-3" />
            1mp
          </span>
        </div>
        <p className="mt-1.5 text-[11px] text-slate-400">
          Készenléti állapot — nincs aktív tranzakció
        </p>
      </DrawerHeader>

      <div className="flex-1 overflow-y-auto">
        {error ? (
          <DrawerSection title="Hiba" icon={ShieldAlert} iconClass="text-rose-500">
            <p className="break-words text-[12px] text-rose-600">
              {String(error)}
            </p>
          </DrawerSection>
        ) : !drivers ? (
          <DrawerSection title="Betöltés" icon={Hourglass} iconClass="text-slate-400">
            <p className="text-[12px] italic text-slate-400">
              driver állapot lekérése…
            </p>
          </DrawerSection>
        ) : (
          <>
            <DriverSection
              label="Hopper"
              icon={Coins}
              result={drivers.coin}
            />
            <DriverSection
              label="Papírpénz olvasó"
              icon={Banknote}
              result={drivers.note}
            />
            <PrinterSection result={drivers.printer} />
          </>
        )}
      </div>
    </>
  );
}

function DriverSection({
  label,
  icon: Icon,
  result,
}: {
  label: string;
  icon: typeof Coins;
  result: CashDriverStatusResult;
}) {
  if (!result.ok) {
    return (
      <DrawerSection
        title={label}
        icon={Icon}
        iconClass="text-rose-500"
        right={<StatusPill tone="bad" icon={WifiOff} label="nem elérhető" />}
      >
        <p className="break-words text-[12px] text-rose-600">{result.error}</p>
      </DrawerSection>
    );
  }

  const { device, plugged_in, setup, levels, error } = result.status;
  const isError = !!error;
  const tone = isError ? "bad" : plugged_in ? "good" : "warn";
  const pillLabel = isError ? "hiba" : plugged_in ? "csatlakoztatva" : "leválasztva";
  const pillIcon = isError ? AlertTriangle : plugged_in ? Power : WifiOff;
  const channels = extractChannels(setup);
  const levelEntries = extractLevels(levels);

  return (
    <DrawerSection
      title={label}
      icon={Icon}
      iconClass={cn(
        isError ? "text-rose-500" : plugged_in ? "text-emerald-500" : "text-amber-500",
      )}
      right={<StatusPill tone={tone} icon={pillIcon} label={pillLabel} />}
    >
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <Chip label="Eszköz" value={formatDeviceName(device)} mono />
          {setup != null && (
            <Chip label="Konfig" value="kész" tone="good" />
          )}
          {channels && (
            <Chip
              label="Címletek"
              value={`${channels.length} csatorna`}
              tone="info"
            />
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 ring-1 ring-rose-100">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-rose-500" />
            <p className="break-words text-[11.5px] text-rose-700">{error}</p>
          </div>
        )}

        {channels && channels.length > 0 && (
          <ChannelStrip channels={channels} />
        )}

        {levelEntries && levelEntries.length > 0 ? (
          <DenominationLadder entries={levelEntries} />
        ) : (
          levels !== null &&
          levels !== undefined && (
            <p className="text-[11px] italic text-slate-400">
              készletszint nem értelmezhető
            </p>
          )
        )}
      </div>
    </DrawerSection>
  );
}

function PrinterSection({ result }: { result: PrinterStatusResult }) {
  if (!result.ok) {
    return (
      <DrawerSection
        title="Nyomtató"
        icon={Printer}
        iconClass="text-rose-500"
        right={<StatusPill tone="bad" icon={WifiOff} label="nem elérhető" />}
      >
        <p className="break-words text-[12px] text-rose-600">{result.error}</p>
      </DrawerSection>
    );
  }

  const { Success, Description, Timestamp, Diagnostic, Events } = result.status;
  const hasError = !printerStatusIsHealthy(result.status);
  const printerEvents = Object.entries(Events ?? {})
    .sort(([, a], [, b]) => b.LastOccurred - a.LastOccurred)
    .slice(0, 3);

  return (
    <DrawerSection
      title="Nyomtató"
      icon={Printer}
      iconClass={hasError ? "text-rose-500" : "text-emerald-500"}
      right={
        <StatusPill
          tone={hasError ? "bad" : "good"}
          icon={hasError ? AlertTriangle : CheckCircle2}
          label={hasError ? "hiba" : "rendben"}
        />
      }
    >
      <div className="space-y-2.5">
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <Chip
            label="Önteszt"
            value={Diagnostic?.Status ?? (Success ? "OK" : "FAIL")}
            tone={hasError ? "bad" : "good"}
          />
          <Chip
            label="Időbélyeg"
            value={format(Timestamp * 1000, "HH:mm:ss")}
            mono
          />
        </div>

        {Diagnostic && (
          <div className="grid grid-cols-3 gap-1.5">
            <PrinterStateTile
              label="Papír"
              ok={Diagnostic.PaperPresent}
              onText="töltve"
              offText="üres"
            />
            <PrinterStateTile
              label="Fedél"
              ok={Diagnostic.CoverClosed}
              onText="zárva"
              offText="nyitva"
            />
            <PrinterStateTile
              label="Fogytán"
              ok={Diagnostic.PaperNearEnd !== true}
              onText="nem"
              offText="igen"
            />
          </div>
        )}

        {!isTrivialPrinterMessage(Description) && (
          <p
            className={cn(
              "border-l-2 pl-2 text-[11.5px] italic",
              hasError
                ? "border-rose-300 text-rose-600"
                : "border-slate-200 text-slate-500",
            )}
          >
            {Description}
          </p>
        )}

        {Diagnostic && (
          <PrinterDiagnosticStrip
            statuses={[
              { label: "Nyomtató", status: Diagnostic.Printer },
              { label: "Offline ok", status: Diagnostic.Offline },
              { label: "Hibajelző", status: Diagnostic.Error },
              { label: "Papír szenzor", status: Diagnostic.PaperRoll },
            ]}
          />
        )}

        {printerEvents.length > 0 && (
          <div className="mt-1 space-y-1">
            <h4 className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
              Utolsó események
            </h4>
            {printerEvents.map(([key, event]) => (
              <PrinterEventCard key={key} eventKey={key} event={event} />
            ))}
          </div>
        )}
      </div>
    </DrawerSection>
  );
}

function PrinterDiagnosticStrip({
  statuses,
}: {
  statuses: Array<{ label: string; status: PrinterDiagnosticStatus | undefined }>;
}) {
  const faults = statuses.filter(
    (s): s is { label: string; status: PrinterDiagnosticStatus } =>
      s.status !== undefined && s.status.HasError,
  );

  if (faults.length === 0) return null;

  return (
    <ul className="space-y-1">
      {faults.map(({ label, status }) => (
        <li
          key={label}
          className="flex items-start gap-2 rounded-md bg-rose-50 px-2.5 py-1.5 ring-1 ring-rose-100"
        >
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-rose-500" />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-rose-700">
                {label}
              </span>
              <span className="font-mono text-[9.5px] tabular-nums text-rose-400">
                {formatPrinterCode(status.Code)}
              </span>
            </div>
            <p className="break-words text-[11.5px] text-rose-700">
              {status.Message}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function isTrivialPrinterMessage(message: string): boolean {
  const m = message.trim().toLowerCase();
  return m === "" || m === "ok" || m === "no error" || m === "no errors";
}

function PrinterEventCard({
  eventKey,
  event,
}: {
  eventKey: string;
  event: PrinterEventDetails;
}) {
  const isFailure = eventKey.includes("FAILED");

  return (
    <div
      className={cn(
        "rounded-lg px-3 py-2 ring-1",
        isFailure ? "bg-rose-50/70 ring-rose-100" : "bg-slate-50 ring-slate-100",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          {isFailure ? (
            <XCircle className="size-3 text-rose-500" />
          ) : (
            <CheckCircle2 className="size-3 text-slate-500" />
          )}
          <span
            className={cn(
              "font-mono text-[10.5px] font-semibold uppercase tracking-[0.16em]",
              isFailure ? "text-rose-700" : "text-slate-700",
            )}
          >
            {event.Event}
          </span>
        </div>
        <span className="font-mono text-[10px] text-slate-400 tabular-nums">
          {format(event.LastOccurred * 1000, "HH:mm:ss")}
        </span>
      </div>
      <p className="mt-0.5 break-words text-[11.5px] text-slate-600">
        {event.Message}
      </p>
      <div className="mt-1 flex flex-wrap gap-1 text-[10.5px]">
        <Chip label="Összesen" value={String(event.OverallCount)} tone={isFailure ? "bad" : "muted"} />
        <Chip
          label="Indítás óta"
          value={String(event.CountSinceDriverRestart)}
          tone="muted"
        />
        <Chip label="Eltelt" value={`${event.LastDiff}mp`} tone="muted" />
      </div>
    </div>
  );
}

function DiagDrawerBody({
  status,
  log,
}: {
  status: CashPaymentStatusView;
  log: LogEntry[];
}) {
  const reversedLog = useMemo(() => [...log].reverse(), [log]);

  const result = resultMeta(status.resultCode);
  const phaseLabel = status.phase === "pay-in" ? "Befizetés" : "Visszafizetés";

  return (
    <>
      <DrawerHeader className="border-b border-slate-200 bg-white/60">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="relative inline-flex size-2.5">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-teal-400 opacity-60" />
              <span className="relative inline-flex size-2.5 rounded-full bg-teal-500 shadow-[0_0_10px_rgba(20,184,166,0.6)]" />
            </span>
            <DrawerTitle className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-700">
              Készpénz · Diagnosztika
            </DrawerTitle>
          </div>
          <div
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] ring-1",
              result.tone === "good"
                ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                : result.tone === "bad"
                  ? "bg-rose-50 text-rose-700 ring-rose-200"
                  : result.tone === "warn"
                    ? "bg-amber-50 text-amber-700 ring-amber-200"
                    : "bg-slate-100 text-slate-600 ring-slate-200",
            )}
          >
            <result.icon className="size-3" />
            {result.label}
          </div>
        </div>
        <div className="mt-2 text-[12px] font-medium text-slate-700">
          {phaseLabel}
          <span className="ml-1.5 text-[11px] text-slate-400">
            · {kindLabel(status.kind)}
          </span>
        </div>
        <p className="mt-0.5 break-all font-mono text-[10px] text-slate-400">
          {status.paymentKey}
        </p>
      </DrawerHeader>

      <div className="flex-1 overflow-y-auto">
        <DrawerSection title="Állapot" icon={Settings2}>
          <div className="grid grid-cols-2 gap-2">
            <StateTile
              label="Befizetés"
              value={payInStateLabel(status.payInState)}
              tone={payInTone(status.payInState)}
            />
            <StateTile
              label="Visszafizetés"
              value={
                status.payOutState ? payOutStateLabel(status.payOutState) : "—"
              }
              tone={status.payOutState ? payOutTone(status.payOutState) : "muted"}
            />
            <StateTile
              label="Mód"
              value={payoutModeLabel(status.payoutMode)}
              tone="muted"
            />
            <StateTile
              label="Kért összeg"
              value={
                status.requestedAmount != null
                  ? `${formatHuf(status.requestedAmount)} Ft`
                  : "—"
              }
              tone="muted"
            />
          </div>
        </DrawerSection>

        <DrawerSection title="Összegek" icon={Banknote}>
          <div className="grid grid-cols-2 gap-2">
            <AmountTile label="Bedobva" value={status.insertedAmount} accent />
            <AmountTile label="Kiadva" value={status.dispensedAmount} />
            <AmountTile label="Visszajáró" value={status.changeAmount} />
            <AmountTile label="Jóváírva" value={status.creditedAmount} />
          </div>
          {status.result && (
            <DenominationBreakdown result={status.result} />
          )}
        </DrawerSection>

        <DrawerSection
          title="Események"
          icon={Timer}
          right={
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
              újabb felül · {log.length}
            </span>
          }
        >
          {reversedLog.length === 0 ? (
            <p className="text-[12px] italic text-slate-400">
              még nincs esemény
            </p>
          ) : (
            <ol className="space-y-1.5">
              {reversedLog.map((entry) => (
                <DrawerEventRow key={entry.id} entry={entry} />
              ))}
            </ol>
          )}
        </DrawerSection>
      </div>
    </>
  );
}

function DenominationBreakdown({
  result,
}: {
  result: NonNullable<CashPaymentStatusView["result"]>;
}) {
  const entered = Object.entries(result.entered.byDenominations).filter(
    ([, n]) => n > 0,
  );
  const dispensed = Object.entries(result.dispensed.byDenominations).filter(
    ([, n]) => n > 0,
  );

  if (entered.length === 0 && dispensed.length === 0) {
    return null;
  }

  return (
    <div className="mt-2.5 grid gap-2">
      {entered.length > 0 && (
        <DenominationGroup
          title="Befizetett címletek"
          icon={ArrowDownCircle}
          accent="teal"
          entries={entered.map(([value, count]) => ({
            value: Number(value),
            count,
          }))}
        />
      )}
      {dispensed.length > 0 && (
        <DenominationGroup
          title="Kiadott címletek"
          icon={ArrowUpCircle}
          accent="amber"
          entries={dispensed.map(([value, count]) => ({
            value: Number(value),
            count,
          }))}
        />
      )}
    </div>
  );
}

function DenominationGroup({
  title,
  icon: Icon,
  accent,
  entries,
}: {
  title: string;
  icon: typeof ArrowDownCircle;
  accent: "teal" | "amber";
  entries: Array<{ value: number; count: number }>;
}) {
  const sorted = [...entries].sort((a, b) => b.value - a.value);
  const total = sorted.reduce((sum, e) => sum + e.value * e.count, 0);
  const accentRing =
    accent === "teal" ? "ring-teal-100 bg-teal-50/30" : "ring-amber-100 bg-amber-50/30";
  const accentText =
    accent === "teal" ? "text-teal-700" : "text-amber-700";
  const accentBar =
    accent === "teal" ? "bg-teal-400" : "bg-amber-400";

  return (
    <div className={cn("rounded-lg px-2.5 py-2 ring-1", accentRing)}>
      <div className="mb-1.5 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className={cn("size-3", accentText)} />
          <span
            className={cn(
              "font-mono text-[10px] font-semibold uppercase tracking-[0.18em]",
              accentText,
            )}
          >
            {title}
          </span>
        </div>
        <span className={cn("font-mono text-[11px] tabular-nums", accentText)}>
          {formatHuf(total)} Ft
        </span>
      </div>
      <div className="space-y-1">
        {sorted.map((e) => (
          <div key={e.value} className="flex items-center gap-2">
            <span className="w-14 shrink-0 text-right font-mono text-[11px] tabular-nums text-slate-700">
              {formatHuf(e.value)}
            </span>
            <span className="text-[9px] text-slate-400">×</span>
            <span className="w-6 font-mono text-[11px] tabular-nums text-slate-600">
              {e.count}
            </span>
            <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-white/60">
              <div
                className={cn("absolute inset-y-0 left-0 rounded-full", accentBar)}
                style={{
                  width: `${Math.min(100, e.count * 14)}%`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChannelStrip({ channels }: { channels: ChannelInfo[] }) {
  const values = [...channels]
    .map((c) => c.value)
    .filter((v) => v > 0)
    .sort((a, b) => a - b);

  if (values.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-slate-400">
        elfogad
      </span>
      {values.map((v, i) => (
        <span
          key={`${v}-${i}`}
          className="inline-flex items-center rounded bg-white px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-slate-600 ring-1 ring-slate-200"
        >
          {formatHuf(v)}
        </span>
      ))}
    </div>
  );
}

function DenominationLadder({
  entries,
}: {
  entries: ReadonlyArray<{ value: number; amount: number }>;
}) {
  const sorted = [...entries].sort((a, b) => b.value - a.value);
  const max = Math.max(1, ...sorted.map((e) => e.amount));
  const total = sorted.reduce((sum, e) => sum + e.value * e.amount, 0);

  return (
    <div className="rounded-lg bg-white px-2.5 py-2 ring-1 ring-slate-100">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-slate-500">
          Készletszint
        </span>
        <span className="font-mono text-[11px] tabular-nums text-slate-700">
          {formatHuf(total)} Ft
        </span>
      </div>
      <div className="space-y-1">
        {sorted.map((e) => {
          const widthPct = (e.amount / max) * 100;
          const tone =
            e.amount === 0
              ? "bg-rose-300"
              : e.amount < max * 0.25
                ? "bg-amber-400"
                : "bg-emerald-400";
          return (
            <div key={e.value} className="flex items-center gap-2">
              <span className="w-14 shrink-0 text-right font-mono text-[11px] tabular-nums text-slate-700">
                {formatHuf(e.value)}
              </span>
              <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={cn("absolute inset-y-0 left-0 rounded-full", tone)}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
              <span className="w-8 text-right font-mono text-[11px] tabular-nums text-slate-600">
                {e.amount}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DrawerSection({
  title,
  icon: Icon,
  iconClass,
  right,
  children,
}: {
  title: string;
  icon?: typeof Settings2;
  iconClass?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="border-b border-slate-200 px-5 py-4 last:border-b-0">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">
          {Icon && <Icon className={cn("size-3.5", iconClass ?? "text-slate-400")} />}
          {title}
        </h3>
        {right}
      </div>
      {children}
    </section>
  );
}

function DrawerEventRow({ entry }: { entry: LogEntry }) {
  const isPayIn = entry.source === "pay-in";
  const meta = describeEvent(entry.type, entry.data);

  return (
    <li
      className={cn(
        "group relative flex items-start gap-3 overflow-hidden rounded-lg px-3 py-2 ring-1 transition",
        meta.severity === "bad"
          ? "bg-rose-50/60 ring-rose-100"
          : meta.severity === "warn"
            ? "bg-amber-50/60 ring-amber-100"
            : "bg-white ring-slate-100",
      )}
    >
      <span
        className={cn(
          "absolute inset-y-0 left-0 w-0.5",
          isPayIn ? "bg-teal-400" : "bg-amber-400",
        )}
      />
      <meta.Icon className={cn("mt-0.5 size-4 shrink-0", meta.iconClass)} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[12.5px] font-semibold text-slate-800">
            {meta.label}
          </span>
          <span className="font-mono text-[10px] text-slate-400 tabular-nums">
            {format(entry.ts, "HH:mm:ss.SSS")}
          </span>
        </div>
        {meta.summary && (
          <div className="mt-0.5 break-words text-[11.5px] leading-snug text-slate-500">
            {meta.summary}
          </div>
        )}
        {meta.chips && meta.chips.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {meta.chips.map((c, i) => (
              <span
                key={i}
                className={cn(
                  "inline-flex items-center rounded bg-white px-1.5 py-0.5 font-mono text-[10px] tabular-nums ring-1",
                  c.tone === "good"
                    ? "text-emerald-700 ring-emerald-200"
                    : c.tone === "bad"
                      ? "text-rose-700 ring-rose-200"
                      : c.tone === "warn"
                        ? "text-amber-700 ring-amber-200"
                        : "text-slate-600 ring-slate-200",
                )}
              >
                {c.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

type Tone = "info" | "warn" | "good" | "bad" | "muted";

function printerStatusIsHealthy(status: NormalizedPrinterStatus): boolean {
  if (!status.Success) {
    return false;
  }

  if (status.Diagnostic) {
    return status.Diagnostic.Status === "OK";
  }

  return !status.Description.toLowerCase().startsWith("error");
}

function formatPrinterCode(code: number): string {
  return `0x${code.toString(16).toUpperCase().padStart(2, "0")}`;
}

function formatHuf(value: number): string {
  return value.toLocaleString("hu-HU");
}

const toneClasses: Record<Tone, string> = {
  info: "bg-sky-50 text-sky-700 ring-sky-200",
  warn: "bg-amber-50 text-amber-700 ring-amber-200",
  good: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  bad: "bg-rose-50 text-rose-700 ring-rose-200",
  muted: "bg-slate-50 text-slate-600 ring-slate-200",
};

function Chip({
  label,
  value,
  tone = "muted",
  mono = false,
}: {
  label: string;
  value: string;
  tone?: Tone;
  mono?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] ring-1",
        toneClasses[tone],
      )}
    >
      <span className="font-mono text-[9px] uppercase tracking-[0.18em] opacity-70">
        {label}
      </span>
      <span className={cn(mono ? "font-mono tabular-nums" : "", "font-medium")}>
        {value}
      </span>
    </span>
  );
}

function StatusPill({
  tone,
  icon: Icon,
  label,
}: {
  tone: Tone;
  icon: typeof CheckCircle2;
  label: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] ring-1",
        toneClasses[tone],
      )}
    >
      <Icon className="size-3" />
      {label}
    </span>
  );
}

function StateTile({
  label,
  value,
  tone = "muted",
}: {
  label: string;
  value: string;
  tone?: Tone;
}) {
  return (
    <div className="rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-slate-100">
      <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-slate-400">
        {label}
      </div>
      <div
        className={cn(
          "mt-0.5 inline-flex items-center rounded px-1.5 py-0.5 text-[11.5px] font-medium ring-1",
          toneClasses[tone],
        )}
      >
        {value}
      </div>
    </div>
  );
}

function PrinterStateTile({
  label,
  ok,
  onText,
  offText,
}: {
  label: string;
  ok: boolean;
  onText: string;
  offText: string;
}) {
  return (
    <div
      className={cn(
        "rounded-lg px-2 py-1.5 ring-1",
        ok ? "bg-emerald-50/50 ring-emerald-100" : "bg-rose-50/50 ring-rose-100",
      )}
    >
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">
        {label}
      </div>
      <div className="mt-0.5 flex items-center gap-1">
        <span
          className={cn(
            "size-1.5 rounded-full",
            ok ? "bg-emerald-500" : "bg-rose-500",
          )}
        />
        <span
          className={cn(
            "text-[11px] font-medium",
            ok ? "text-emerald-700" : "text-rose-700",
          )}
        >
          {ok ? onText : offText}
        </span>
      </div>
    </div>
  );
}

function AmountTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-lg px-3 py-2 ring-1",
        accent
          ? "bg-gradient-to-br from-teal-50 to-white ring-teal-100"
          : "bg-white ring-slate-100",
      )}
    >
      <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-slate-400">
        {label}
      </div>
      <div className="mt-0.5 tabular-nums">
        <span
          className={cn(
            "text-lg font-bold tracking-tight",
            accent ? "text-teal-700" : "text-slate-800",
          )}
        >
          {formatHuf(value)}
        </span>
        <span className="ml-1 font-mono text-[10px] text-slate-400">Ft</span>
      </div>
    </div>
  );
}

// --- Translation & humanization ---------------------------------------------

function kindLabel(kind: string): string {
  switch (kind) {
    case "purchase":
      return "Vásárlás";
    case "balance_topup":
      return "Egyenleg feltöltés";
    default:
      return kind;
  }
}

function payInStateLabel(state: string): string {
  switch (state) {
    case "starting":
      return "indul";
    case "active":
      return "aktív";
    case "ending":
      return "záródik";
    case "ended":
      return "lezárva";
    default:
      return state;
  }
}

function payOutStateLabel(state: string): string {
  switch (state) {
    case "starting":
      return "indul";
    case "active":
      return "aktív";
    case "ended":
      return "lezárva";
    case "failed":
      return "hibás";
    default:
      return state;
  }
}

function payInTone(state: string): Tone {
  switch (state) {
    case "starting":
      return "info";
    case "active":
      return "warn";
    case "ending":
      return "info";
    case "ended":
      return "good";
    default:
      return "muted";
  }
}

function payOutTone(state: string): Tone {
  switch (state) {
    case "starting":
      return "info";
    case "active":
      return "warn";
    case "ended":
      return "good";
    case "failed":
      return "bad";
    default:
      return "muted";
  }
}

function payoutModeLabel(mode: string | null | undefined): string {
  switch (mode) {
    case "change":
      return "visszajáró";
    case "refund":
      return "visszatérítés";
    case "none":
      return "nincs";
    default:
      return "—";
  }
}

function resultMeta(code: string): {
  label: string;
  tone: Tone;
  icon: typeof CheckCircle2;
} {
  switch (code) {
    case "pending":
      return { label: "folyamatban", tone: "info", icon: Hourglass };
    case "success":
      return { label: "sikeres", tone: "good", icon: CheckCircle2 };
    case "aborted":
      return { label: "megszakítva", tone: "warn", icon: XCircle };
    case "timed_out":
      return { label: "időtúllépés", tone: "warn", icon: Timer };
    case "fatal_error":
      return { label: "hardver hiba", tone: "bad", icon: AlertTriangle };
    case "aborted_but_payout_failed":
      return { label: "megszakítva · payout hibás", tone: "bad", icon: AlertTriangle };
    case "success_but_payout_failed":
      return { label: "sikeres · payout hibás", tone: "bad", icon: AlertTriangle };
    default:
      return { label: code, tone: "muted", icon: Settings2 };
  }
}

function formatDeviceName(device: string): string {
  return device
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

type ChannelInfo = { value: number; country: string | undefined };

function extractChannels(setup: unknown): ChannelInfo[] | null {
  if (!setup || typeof setup !== "object") return null;
  const channels = (setup as { channels?: unknown }).channels;
  if (!Array.isArray(channels)) return null;
  return channels
    .map((c): ChannelInfo | null => {
      if (!c || typeof c !== "object") return null;
      const value = (c as { value?: unknown }).value;
      const country = (c as { country_code?: unknown }).country_code;
      if (typeof value !== "number") return null;
      return {
        value,
        country: typeof country === "string" ? country : undefined,
      };
    })
    .filter((c): c is ChannelInfo => c !== null);
}

function extractLevels(
  levels: unknown,
): ReadonlyArray<{ value: number; amount: number }> | null {
  if (!levels || typeof levels !== "object") return null;
  const entries = (levels as { entries?: unknown }).entries;
  if (!Array.isArray(entries)) return null;
  return entries
    .map((e) => {
      if (!e || typeof e !== "object") return null;
      const value = (e as { value?: unknown }).value;
      const amount = (e as { amount?: unknown }).amount;
      if (typeof value !== "number" || typeof amount !== "number") return null;
      return { value, amount };
    })
    .filter((e): e is { value: number; amount: number } => e !== null);
}

type EventChip = { label: string; tone?: Tone };

type EventMeta = {
  label: string;
  Icon: typeof Banknote;
  iconClass: string;
  severity: "info" | "warn" | "bad";
  summary?: string;
  chips?: EventChip[];
};

function describeEvent(type: string, data: unknown): EventMeta {
  switch (type) {
    case "coin_credit": {
      const v = readNumber(data, "value");
      const routing = readString(data, "routing") ?? "hopper";
      return {
        label: "Érme befogadva",
        Icon: Coins,
        iconClass: "text-teal-600",
        severity: "info",
        summary: v != null ? `${formatHuf(v)} Ft érme` : undefined,
        chips: [
          ...(v != null
            ? [{ label: `${formatHuf(v)} Ft`, tone: "good" as Tone }]
            : []),
          { label: routing === "cashbox" ? "kasszába" : "hopper" },
        ],
      };
    }
    case "note_credit": {
      const v = readNumber(data, "value");
      return {
        label: "Bankjegy befogadva",
        Icon: Banknote,
        iconClass: "text-teal-600",
        severity: "info",
        summary: v != null ? `${formatHuf(v)} Ft bankjegy` : undefined,
        chips:
          v != null
            ? [{ label: `${formatHuf(v)} Ft`, tone: "good" as Tone }]
            : undefined,
      };
    }
    case "note_read": {
      const v = readNumber(data, "value");
      return {
        label: "Bankjegy beolvasva",
        Icon: Banknote,
        iconClass: "text-sky-500",
        severity: "info",
        summary: v != null ? `${formatHuf(v)} Ft felismerve` : undefined,
      };
    }
    case "dispensing":
    case "dispensed": {
      const list = readCountryValues(data);
      const total = list.reduce((s, e) => s + (e.value ?? 0), 0);
      return {
        label: type === "dispensing" ? "Kiadás folyamatban" : "Kiadva",
        Icon: ArrowUpCircle,
        iconClass: "text-amber-600",
        severity: "info",
        summary:
          list.length > 0
            ? `${formatHuf(total)} Ft · ${list.length} címlet`
            : undefined,
        chips: list.slice(0, 6).map((e) => ({
          label: `${formatHuf(e.value)} Ft`,
          tone: "warn" as Tone,
        })),
      };
    }
    case "floating":
    case "floated": {
      return {
        label: type === "floating" ? "Float folyamatban" : "Float kész",
        Icon: Layers,
        iconClass: "text-slate-500",
        severity: "info",
      };
    }
    case "rejecting":
      return {
        label: "Elutasítás",
        Icon: XCircle,
        iconClass: "text-amber-500",
        severity: "warn",
      };
    case "rejected":
      return {
        label: "Elutasítva",
        Icon: XCircle,
        iconClass: "text-amber-600",
        severity: "warn",
      };
    case "stacking":
      return {
        label: "Tárolás",
        Icon: Layers,
        iconClass: "text-slate-500",
        severity: "info",
      };
    case "stacked":
      return {
        label: "Tárolva",
        Icon: CheckCircle2,
        iconClass: "text-slate-500",
        severity: "info",
      };
    case "reading":
      return {
        label: "Olvasás",
        Icon: RefreshCw,
        iconClass: "text-sky-500",
        severity: "info",
      };
    case "initialising":
      return {
        label: "Inicializálás",
        Icon: RefreshCw,
        iconClass: "text-slate-500",
        severity: "info",
      };
    case "disabled":
      return {
        label: "Letiltva",
        Icon: WifiOff,
        iconClass: "text-slate-400",
        severity: "info",
      };
    case "slave_reset":
      return {
        label: "Slave reset",
        Icon: Power,
        iconClass: "text-amber-600",
        severity: "warn",
      };
    case "fraud_attempt":
      return {
        label: "Csalási kísérlet",
        Icon: ShieldAlert,
        iconClass: "text-rose-600",
        severity: "bad",
      };
    case "safe_jam":
      return {
        label: "Biztonsági elakadás",
        Icon: AlertTriangle,
        iconClass: "text-amber-600",
        severity: "warn",
      };
    case "unsafe_jam":
      return {
        label: "Veszélyes elakadás",
        Icon: AlertTriangle,
        iconClass: "text-rose-600",
        severity: "bad",
      };
    case "stacker_full":
      return {
        label: "Tároló megtelt",
        Icon: AlertTriangle,
        iconClass: "text-amber-600",
        severity: "warn",
      };
    case "coins_low":
      return {
        label: "Kevés érme",
        Icon: AlertTriangle,
        iconClass: "text-amber-600",
        severity: "warn",
      };
    case "hopper_jammed":
      return {
        label: "Hopper beragadt",
        Icon: AlertTriangle,
        iconClass: "text-rose-600",
        severity: "bad",
      };
    case "halted":
      return {
        label: "Leállítva",
        Icon: Power,
        iconClass: "text-rose-500",
        severity: "bad",
      };
    case "timeout":
      return {
        label: "Időtúllépés",
        Icon: Timer,
        iconClass: "text-amber-600",
        severity: "warn",
      };
    case "incomplete_payout":
    case "incomplete_float": {
      const list = readCountryPayouts(data);
      const missing = list.reduce(
        (s, e) => s + Math.max(0, e.requested - e.dispensed),
        0,
      );
      return {
        label:
          type === "incomplete_payout"
            ? "Részleges kifizetés"
            : "Részleges float",
        Icon: AlertTriangle,
        iconClass: "text-rose-600",
        severity: "bad",
        summary:
          missing > 0 ? `Hiányzó: ${formatHuf(missing)} Ft` : undefined,
      };
    }
    case "cashbox_paid": {
      const list = readCountryValues(data);
      const total = list.reduce((s, e) => s + e.value, 0);
      return {
        label: "Kasszába került",
        Icon: HardDrive,
        iconClass: "text-slate-500",
        severity: "info",
        summary: total > 0 ? `${formatHuf(total)} Ft` : undefined,
      };
    }
    case "coin_mech_jammed":
      return {
        label: "Érme mechanika elakadt",
        Icon: AlertTriangle,
        iconClass: "text-rose-600",
        severity: "bad",
      };
    case "coin_mech_return_active":
      return {
        label: "Érme visszajuttatás",
        Icon: ArrowDownCircle,
        iconClass: "text-amber-500",
        severity: "warn",
      };
    case "coin_mech_error": {
      const msg = readString(data, "message") ?? readString(data, "Message");
      return {
        label: "Érme mechanika hiba",
        Icon: AlertTriangle,
        iconClass: "text-rose-600",
        severity: "bad",
        summary: msg,
      };
    }
    case "lid_open":
      return {
        label: "Fedél nyitva",
        Icon: AlertTriangle,
        iconClass: "text-amber-600",
        severity: "warn",
      };
    case "lid_closed":
      return {
        label: "Fedél zárva",
        Icon: CheckCircle2,
        iconClass: "text-emerald-500",
        severity: "info",
      };
    case "emptying":
    case "emptied":
    case "smart_emptying":
    case "smart_emptied":
      return {
        label: type.includes("smart")
          ? type.endsWith("ing")
            ? "Okos ürítés"
            : "Okos ürítés kész"
          : type.endsWith("ing")
            ? "Ürítés"
            : "Ürítés kész",
        Icon: Layers,
        iconClass: "text-slate-500",
        severity: "info",
      };
    case "calibration_failed":
      return {
        label: "Kalibrálás sikertelen",
        Icon: AlertTriangle,
        iconClass: "text-rose-600",
        severity: "bad",
      };
    case "attached_coin_mech_disabled":
      return {
        label: "Érme mechanika kikapcs.",
        Icon: WifiOff,
        iconClass: "text-slate-400",
        severity: "info",
      };
    case "attached_coin_mech_enabled":
      return {
        label: "Érme mechanika bekapcs.",
        Icon: CheckCircle2,
        iconClass: "text-emerald-500",
        severity: "info",
      };
    case "info": {
      const msg = typeof data === "string" ? data : undefined;
      return {
        label: "Info",
        Icon: Settings2,
        iconClass: "text-slate-400",
        severity: "info",
        summary: msg,
      };
    }
    case "unknown":
      return {
        label: "Ismeretlen esemény",
        Icon: AlertTriangle,
        iconClass: "text-slate-400",
        severity: "warn",
        summary: typeof data === "number" ? `kód: ${data}` : undefined,
      };
    default:
      return {
        label: prettifyType(type),
        Icon: Settings2,
        iconClass: "text-slate-400",
        severity: "info",
      };
  }
}

function prettifyType(t: string): string {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function readNumber(data: unknown, key: string): number | undefined {
  if (!data || typeof data !== "object") return undefined;
  const v = (data as Record<string, unknown>)[key];
  return typeof v === "number" ? v : undefined;
}

function readString(data: unknown, key: string): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const v = (data as Record<string, unknown>)[key];
  return typeof v === "string" ? v : undefined;
}

type CountryValueLike = { value: number; country: string | undefined };
type CountryPayoutLike = {
  dispensed: number;
  requested: number;
  country: string | undefined;
};

function readCountryValues(data: unknown): CountryValueLike[] {
  if (!Array.isArray(data)) return [];
  return data
    .map((e): CountryValueLike | null => {
      if (!e || typeof e !== "object") return null;
      const value = (e as { value?: unknown }).value;
      const country = (e as { country?: unknown }).country;
      if (typeof value !== "number") return null;
      return {
        value,
        country: typeof country === "string" ? country : undefined,
      };
    })
    .filter((e): e is CountryValueLike => e !== null);
}

function readCountryPayouts(data: unknown): CountryPayoutLike[] {
  if (!Array.isArray(data)) return [];
  return data
    .map((e): CountryPayoutLike | null => {
      if (!e || typeof e !== "object") return null;
      const dispensed = (e as { dispensed?: unknown }).dispensed;
      const requested = (e as { requested?: unknown }).requested;
      const country = (e as { country?: unknown }).country;
      if (typeof dispensed !== "number" || typeof requested !== "number") {
        return null;
      }
      return {
        dispensed,
        requested,
        country: typeof country === "string" ? country : undefined,
      };
    })
    .filter((e): e is CountryPayoutLike => e !== null);
}
